import cn from 'classnames';
import { AnimatePresence, motion, PanInfo } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import GaugeComponent from 'react-gauge-component';
import { useIntl } from 'react-intl';
import { Switch, Tooltip, TooltipInteractionEnum, useQueryFetch } from 'sonata-react-components';

import { getBidStream } from '@/services/bidStream.service';
import {
  audienceToPercentage,
  getAudienceFilter,
  getAudienceRange,
  getAudienceRangeValue,
  getCartographyFilter
} from './helpers';

import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';

import './floatingAudiencePanel.scss';

type Props = {
  country: string;
  zones: Zone[] | null;
  targeting: TargetingBaseType;
};

type ViewMode = 'icon' | 'summary' | 'full';

type SummaryMetric = {
  icon: string;
  value: string;
  description: string;
};

type State = {
  audienceValue: number | null;
  viewMode: ViewMode;
};

export function FloatingAudiencePanel({ country, targeting, zones }: Props) {
  const { formatMessage } = useIntl();
  const [{ audienceValue, viewMode }, setState] = useState<State>({
    audienceValue: null,
    viewMode: 'icon'
  });
  const dragMovedRef = useRef(false);
  const DRAG_THRESHOLD_PX = 5;

  const cartographyFilter = getCartographyFilter(zones);
  const audienceFilter = getAudienceFilter(targeting);
  const thereAreCartographicZones = Boolean(cartographyFilter?.length);

  const token = {};
  const { isLoading, data: bidStreamData } = useQueryFetch(
    ['bidStream', cartographyFilter, audienceFilter],
    async () => getBidStream(country, { audience: audienceFilter, cartographic: cartographyFilter }, token),
    {
      enabled: thereAreCartographicZones
    }
  );

  useEffect(() => {
    if (thereAreCartographicZones) {
      return;
    }
    if (viewMode !== 'icon') {
      setState(prev => ({
        ...prev,
        viewMode: 'icon'
      }));
    }
  }, [thereAreCartographicZones]);

  if (bidStreamData && bidStreamData?.deviceIdsCount !== audienceValue) {
    setState(prev => ({
      ...prev,
      audienceValue: bidStreamData?.deviceIdsCount || 0
    }));
  }

  if (!thereAreCartographicZones) {
    return null;
  }

  const audiencePercentage = audienceToPercentage(audienceValue || 0);
  const audienceRange = getAudienceRange(audienceValue || 0);
  const formattedAudience = getAudienceRangeValue(audienceValue || 0);

  const metrics: SummaryMetric[] = [
    {
      description: formatMessage({
        defaultMessage: 'Campaign user reach',
        id: 'floatingAudiencePanel.campaignReach'
      }),
      icon: isLoading ? 'fa-solid fa-spinner' : 'fa-solid fa-user',
      value: isLoading
        ? formatMessage({
            defaultMessage: 'Loading',
            id: 'floatingAudiencePanel.loading'
          })
        : formattedAudience
    }
    // TODO: Enable when the rest of the panels are added
    // {
    //   icon: 'fa-regular fa-calendar-users',
    //   value: 'N/A',
    //   description: formatMessage({
    //     defaultMessage: 'Daily user reach',
    //     id: 'floatingAudiencePanel.dailyReach'
    //   })
    // },
    // {
    //   icon: 'fa-regular fa-chart-user',
    //   value: 'N/A',
    //   description: formatMessage({
    //     defaultMessage: 'Share of voice',
    //     id: 'floatingAudiencePanel.shareVoice'
    //   })
    // },
  ];

  const getAudienceDescription = () => {
    switch (audienceRange) {
      case 'low':
        return formatMessage({
          defaultMessage:
            'Your audience is too narrow, which may affect ad delivery. Try broadening it by adding more locations to improve performance and reach more people likely to engage.',
          id: 'floatingAudiencePanel.audienceDescription.low'
        });
      case 'middle':
        return formatMessage({
          defaultMessage:
            'Your current audience works, but expanding it may improve performance. Consider adding more locations to connect with even more potential customers.',
          id: 'floatingAudiencePanel.audienceDescription.middle'
        });
      default:
        return formatMessage({
          defaultMessage:
            'Great job! your estimated audience is broad, supporting strong delivery and helping your ads reach more people likely to engage.',
          id: 'floatingAudiencePanel.audienceDescription.high'
        });
    }
  };

  const getGaugeColors = (): [string, string, string] => {
    if (audienceRange === 'low') {
      return ['#db245a', '#ffebcf', '#dfece7'];
    }
    if (audienceRange === 'middle') {
      return ['#f7d3de', '#ffa526', '#dfece7'];
    }
    return ['#f7d3de', '#ffebcf', '#65A990'];
  };

  const handleViewMode = () => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }
    const newViewMode = viewMode === 'icon' ? 'summary' : 'full';
    setState(prev => ({ ...prev, viewMode: newViewMode }));
  };

  const handleClose = (ev: React.SyntheticEvent) => {
    ev.stopPropagation();
    setState(prev => ({
      ...prev,
      viewMode: 'icon'
    }));
  };

  const handleDragAndDrop = (_: Event, info: PanInfo, type: 'start' | 'action') => {
    if (type === 'start') {
      dragMovedRef.current = false;
      return;
    }
    const { x = 0, y = 0 } = info?.offset || {};
    if (Math.abs(x) > DRAG_THRESHOLD_PX || Math.abs(y) > DRAG_THRESHOLD_PX) {
      dragMovedRef.current = true;
    }
  };

  const animationConfig = {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    initial: { opacity: 0 },
    transition: { duration: 0.3 }
  };

  return (
    <motion.div
      className="floatingAudiencePanel"
      drag="y"
      dragConstraints={{ bottom: window.innerHeight - 380, top: 0 }}
      dragElastic={0.1}
      onDrag={(...args) => handleDragAndDrop(...args, 'action')}
      onDragStart={(...args) => handleDragAndDrop(...args, 'start')}
    >
      <AnimatePresence mode="wait">
        <Switch>
          <Switch.Case condition={viewMode === 'full'}>
            <motion.div className="floatingAudiencePanel__full" key="full" {...animationConfig}>
              <div className="floatingAudiencePanel__fullHeader">
                <h5 className="floatingAudiencePanel__fullHeaderTitle">
                  {formatMessage({
                    defaultMessage: 'My campaign summary',
                    id: 'floatingAudiencePanel.campaignSummary'
                  })}
                </h5>
                <span className="floatingAudiencePanel__fullHeaderClose fa-regular fa-xmark" onClick={handleClose} />
              </div>
              <div className="floatingAudiencePanel__fullAudience">
                <div className="floatingAudiencePanel__fullAudienceHeader">
                  <span className="floatingAudiencePanel__fullAudienceHeaderText">
                    <i className="fa-solid fa-user" />
                    {formatMessage({
                      defaultMessage: 'Estimated audience size',
                      id: 'floatingAudiencePanel.audienceSize'
                    })}
                  </span>
                  <Tooltip
                    config={{
                      arrow: true,
                      interactions: [TooltipInteractionEnum.hover],
                      placement: 'top-end',
                      portal: true,
                      strategy: 'fixed',
                      text: formatMessage({
                        defaultMessage:
                          'Shows whether your audience is too narrow, balanced, or broad based on the targeting information you’ve added. This value is an estimate intended to help you understand the potential scope of your selections.',
                        id: 'floatingAudiencePanel.audienceTooltip'
                      })
                    }}
                  >
                    <i className="floatingAudiencePanel__fullAudienceHeaderIconTooltip fa-regular fa-circle-info" />
                  </Tooltip>
                </div>
                <div className="floatingAudiencePanel__fullAudienceContainer">
                  <p className="floatingAudiencePanel__fullAudienceDescription">{getAudienceDescription()}</p>
                  <div className="floatingAudiencePanel__fullAudienceGauge">
                    <GaugeComponent
                      arc={{
                        colorArray: getGaugeColors(),
                        padding: 0.05,
                        subArcs: [{ limit: 33 }, { limit: 66 }, { limit: 100 }]
                      }}
                      className="floatingAudiencePanel__fullAudienceGaugeComponent"
                      labels={{
                        tickLabels: {
                          hideMinMax: true,
                          ticks: []
                        },
                        valueLabel: {
                          hide: true
                        }
                      }}
                      marginInPercent={0.03}
                      pointer={{
                        color: '#3d5787',
                        length: 0.5,
                        width: 10
                      }}
                      type="semicircle"
                      value={audiencePercentage}
                    />
                    <div className="floatingAudiencePanel__fullAudienceGaugeDescription">
                      <span className={cn({ floatingPanelAlert: audienceRange === 'low' })}>
                        {formatMessage({
                          defaultMessage: 'Limited',
                          id: 'floatingAudiencePanel.audience.limited'
                        })}
                      </span>
                      <span className={cn({ floatingPanelSuccess: audienceRange === 'high' })}>
                        {formatMessage({
                          defaultMessage: 'Wide',
                          id: 'floatingAudiencePanel.audience.wide'
                        })}
                      </span>
                    </div>
                    <div className="floatingAudiencePanel__fullAudienceGaugeFooter">
                      <b>{formattedAudience}</b>
                      <span>
                        {formatMessage({
                          defaultMessage: 'user reach',
                          id: 'floatingAudiencePanel.audience.userReach'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </Switch.Case>
          <Switch.Case condition={viewMode === 'summary'}>
            <motion.div
              className="floatingAudiencePanel__summary"
              key="summary"
              onClick={handleViewMode}
              {...animationConfig}
            >
              <Tooltip
                config={{
                  arrow: true,
                  forcePlacement: false,
                  interactions: [TooltipInteractionEnum.hover],
                  placement: 'top',
                  strategy: 'fixed',
                  text: formatMessage({
                    defaultMessage: 'Click to see more information',
                    id: 'floatingAudiencePanel.tooltipText'
                  })
                }}
              >
                <div className="floatingAudiencePanel__summaryTop" onClick={handleViewMode}>
                  <div className="floatingAudiencePanel__summaryTitle">
                    {formatMessage({
                      defaultMessage: 'Estimated summary',
                      id: 'floatingAudiencePanel.summaryTitle'
                    })}
                  </div>
                  <span className="floatingAudiencePanel__summaryClose fa-regular fa-xmark" onClick={handleClose} />
                </div>
              </Tooltip>
              {metrics.map((metric, index) => (
                <div className="floatingAudiencePanel__summaryMetric" key={index}>
                  <i
                    className={cn(
                      'floatingAudiencePanel__summaryMetricIcon',
                      {
                        'floatingAudiencePanel__summaryMetricIcon--rotating': isLoading,
                        floatingPanelAlert: !isLoading && audienceRange === 'low',
                        floatingPanelMiddle: !isLoading && audienceRange === 'middle',
                        floatingPanelSuccess: !isLoading && audienceRange === 'high'
                      },
                      metric.icon
                    )}
                  />
                  <div className="floatingAudiencePanel__summaryMetricTexts">
                    <span
                      className={cn('floatingAudiencePanel__summaryMetricValue', {
                        floatingPanelAlert: !isLoading && audienceRange === 'low',
                        floatingPanelMiddle: !isLoading && audienceRange === 'middle',
                        floatingPanelSuccess: !isLoading && audienceRange === 'high'
                      })}
                    >
                      {metric.value}
                    </span>
                    <span className="floatingAudiencePanel__summaryMetricDescription">{metric.description}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          </Switch.Case>
          <Switch.Default>
            <motion.div
              className="floatingAudiencePanel__icon"
              key="icon"
              onClick={handleViewMode}
              {...animationConfig}
            >
              <span className="fa-solid fa-clipboard-list-check" />
            </motion.div>
          </Switch.Default>
        </Switch>
      </AnimatePresence>
    </motion.div>
  );
}
